<template>
  <section id="branches" class="contact-section">
    <v-container>
      <div class="section-heading">
        <div class="eyebrow">Branches & contact</div>
        <h2>Visit the branch that works best for your schedule.</h2>
        <p>
          Tap a branch card to open Google Maps directions, or call before visiting to confirm service availability, HMO coverage, and final pricing.
        </p>
      </div>

      <v-row>
        <v-col v-for="branch in branches" :key="branch.id" cols="12" md="6">
          <v-card class="branch-card" elevation="0">
            <div class="branch-card__image-wrap">
              <v-img class="branch-card__image" :src="branch.image" :alt="branch.imageAlt" />
              <span>{{ branch.label }}</span>
            </div>
            <div class="branch-card__body">
              <h3><router-link :to="`/branches/${branch.id}`">{{ branch.name }}</router-link></h3>
              <p class="branch-card__address">
                <i class="pi pi-map-marker inline-icon" aria-hidden="true"></i>
                {{ branch.address }}
              </p>
              <div class="branch-card__details">
                <span>
                  <i class="pi pi-clock inline-icon" aria-hidden="true"></i>
                  {{ branch.hours }}
                </span>
                <span>
                  <i class="pi pi-calendar inline-icon" aria-hidden="true"></i>
                  {{ branch.sundayHours }}
                </span>
                <a :href="branch.phoneHref" @click.stop>
                  <i class="pi pi-phone inline-icon" aria-hidden="true"></i>
                  {{ branch.phone }}
                </a>
                <a :href="`mailto:${branch.email}`" @click.stop>
                  <i class="pi pi-envelope inline-icon" aria-hidden="true"></i>
                  {{ branch.email }}
                </a>
              </div>

              <div class="branch-card__services">
                <v-chip v-for="service in branch.services" :key="service" small class="mr-2 mb-2">
                  {{ service }}
                </v-chip>
              </div>

              <div class="branch-card__actions">
                <v-btn class="mayon-btn mayon-btn--primary" :href="branch.mapUrl" target="_blank" rel="noopener noreferrer" @click.stop>
                  Get directions
                </v-btn>
                <v-btn class="mayon-btn mayon-btn--secondary" :to="`/branches/${branch.id}`" @click.stop>
                  Branch details
                </v-btn>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
import { branches } from '@/data/mayonSite'

export default {
  name: 'ContactUs',
  data() {
    return {
      branches
    }
  }
}
</script>

<style lang="scss" scoped>
.contact-section {
  background: linear-gradient(180deg, var(--mayon-bg), #ffffff);
  padding: 5rem 0;
}

.section-heading {
  margin: 0 auto 2.25rem;
  max-width: 760px;
  text-align: center;

  .eyebrow {
    color: var(--mayon-blue);
    font-size: 0.82rem;
    font-weight: 800;
    letter-spacing: 0.09em;
    margin-bottom: 0.7rem;
    text-transform: uppercase;
  }

  h2 {
    color: var(--mayon-navy);
    font-size: clamp(2rem, 4vw, 3rem);
    letter-spacing: -0.035em;
    line-height: 1.1;
    margin-bottom: 1rem;
  }

  p {
    color: var(--mayon-muted);
    line-height: 1.8;
  }
}

.inline-icon {
  color: var(--mayon-blue);
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.95rem;
  line-height: 1.4;
}

.branch-card {
  background: #ffffff !important;
  border: 1px solid var(--mayon-border) !important;
  border-radius: 28px !important;
  box-shadow: none !important;
  cursor: pointer;
  height: 100%;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover,
  &:focus {
    border-color: rgba(88, 184, 184, 0.34) !important;
    box-shadow: var(--mayon-shadow-hover) !important;
    outline: none;
    transform: translateY(-3px);
  }

  &__image-wrap {
    height: 210px;
    overflow: hidden;
    position: relative;

    span {
      backdrop-filter: blur(10px);
      background: rgba(10, 27, 52, 0.86);
      border: 1px solid rgba(255, 255, 255, 0.18);
      border-radius: 999px;
      bottom: 1rem;
      color: #ffffff;
      font-weight: 800;
      left: 1rem;
      padding: 0.45rem 0.8rem;
      position: absolute;
    }
  }

  &__image {
    height: 100%;
    width: 100%;
  }

  &__image::v-deep .v-image__image {
    background-position: center center !important;
  }

  &__body {
    padding: 1.45rem;
  }

  h3 {
    color: var(--mayon-navy);
    font-size: 1.35rem;
    margin-bottom: 0.75rem;
  }

  &__address {
    align-items: flex-start;
    color: #344054;
    display: flex;
    gap: 0.4rem;
    line-height: 1.6;
  }

  &__details {
    display: grid;
    gap: 0.45rem;
    margin: 1rem 0;

    span,
    a {
      align-items: center;
      color: #344054;
      display: inline-flex;
      gap: 0.45rem;
      text-decoration: none;
    }

    a:hover {
      color: var(--mayon-blue);
    }
  }

  &__services {
    margin: 1rem 0;
  }

  &__services::v-deep .v-chip {
    background: var(--mayon-soft) !important;
    border: 1px solid var(--mayon-border) !important;
    color: #344054 !important;
    font-weight: 700;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
  }
}
</style>
